import React from 'react';
import { motion } from 'framer-motion';
import { FaShip, FaTruck, FaUsers, FaClipboardCheck, FaGlobe, FaChartLine } from 'react-icons/fa';
import CallToAction from '../components/shared/CallToAction';

const ServicesPage = () => {
  const services = [
    {
      id: "importation",
      icon: <FaShip className="text-5xl text-primary mb-4" />,
      title: "Importation",
      description: "Nous gérons l'ensemble du processus d'importation, depuis la source jusqu'à l'entrée sur le territoire canadien.",
      features: [
        "Gestion des documents d'importation et de douane",
        "Conformité avec les exigences sanitaires canadiennes",
        "Coordination avec les transitaires qualifiés",
        "Vérification des étiquetages bilingues conformes"
      ]
    },
    {
      id: "distribution",
      icon: <FaTruck className="text-5xl text-primary mb-4" />,
      title: "Distribution",
      description: "Notre réseau de distribution couvre les principaux marchés au Québec et en Ontario avec une expansion continue.",
      features: [
        "Livraison rapide avec un délai moyen de 2 jours",
        "Couverture de 158+ points de vente",
        "70+ livraisons hebdomadaires",
        "Gestion d'entrepôt et d'inventaire"
      ]
    },
    {
      id: "representation",
      icon: <FaUsers className="text-5xl text-primary mb-4" />,
      title: "Représentation",
      description: "Nous représentons votre marque auprès des détaillants et distributeurs canadiens.",
      features: [
        "Négociation avec les chaînes de distribution",
        "Représentation lors des salons professionnels",
        "Stratégies de pricing adaptées au marché local",
        "Gestion des relations clients"
      ]
    },
    {
      id: "supply-chain",
      icon: <FaGlobe className="text-5xl text-primary mb-4" />,
      title: "Chaîne d'approvisionnement",
      description: "Optimisation complète de votre chaîne d'approvisionnement internationale vers le Canada.",
      features: [
        "Planification stratégique des approvisionnements",
        "Gestion des stocks adaptée à la demande",
        "Optimisation des coûts logistiques",
        "Traçabilité des produits de bout en bout"
      ]
    },
    {
      id: "compliance",
      icon: <FaClipboardCheck className="text-5xl text-primary mb-4" />,
      title: "Conformité",
      description: "Nous garantissons que vos produits respectent toutes les normes et réglementations canadiennes.",
      features: [
        "Conformité ACIA (Agence canadienne d'inspection des aliments)",
        "Respect des exigences de l'ASFC (Agence des services frontaliers)",
        "Traduction et étiquetage bilingue",
        "Vérification des normes de qualité et de sécurité"
      ]
    },
    {
      id: "market-growth",
      icon: <FaChartLine className="text-5xl text-primary mb-4" />,
      title: "Stratégie de croissance",
      description: "Nous développons des stratégies personnalisées pour maximiser votre pénétration du marché canadien.",
      features: [
        "Analyse de marché et identification d'opportunités",
        "Stratégies d'expansion vers de nouvelles chaînes",
        "Plans de croissance pour de nouvelles provinces",
        "Optimisation du portefeuille de produits"
      ]
    }
  ];

  const workProcess = [
    {
      number: "01",
      title: "Consultation initiale",
      description: "Analyse de vos produits, objectifs et besoins spécifiques pour le marché canadien."
    },
    {
      number: "02",
      title: "Stratégie personnalisée",
      description: "Développement d'un plan d'action sur mesure pour l'importation et la distribution."
    },
    {
      number: "03",
      title: "Préparation à l'importation",
      description: "Gestion des documents, conformité et logistique pour l'entrée sur le marché."
    },
    {
      number: "04",
      title: "Distribution & représentation",
      description: "Placement des produits dans notre réseau et suivi des performances."
    },
    {
      number: "05",
      title: "Croissance continue",
      description: "Expansion progressive vers de nouveaux points de vente et territoires."
    }
  ];

  return (
    <div className="pt-24">
      {/* Hero Section */}
      <div className="bg-primary text-white py-16">
        <div className="container-custom">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Nos Services</h1>
          <p className="text-xl max-w-2xl">
            Des solutions complètes d'importation, de distribution et de représentation pour développer vos produits au Canada.
          </p>
        </div>
      </div>

      {/* Services Overview */}
      <section className="section">
        <div className="container-custom">
          <h2 className="section-title">Comment nous pouvons vous aider</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto text-center mb-16">
            DZF Distribution vous offre une gamme complète de services pour faciliter votre entrée et votre développement sur le marché canadien.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={service.id}
                id={service.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white p-8 rounded-lg shadow-md flex flex-col h-full"
              >
                <div className="flex justify-center mb-4">
                  {service.icon}
                </div>
                <h3 className="text-2xl font-bold mb-3 text-center">{service.title}</h3>
                <p className="text-gray-600 mb-6 text-center">{service.description}</p>
                <ul className="space-y-3 mt-auto">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start">
                      <div className="bg-primary/10 p-1 rounded-full mr-3 mt-1">
                        <svg className="h-3 w-3 text-primary" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                        </svg>
                      </div>
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How We Work */}
      <section className="section bg-gray-100">
        <div className="container-custom">
          <h2 className="section-title">Notre Processus</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto text-center mb-16">
            Une approche structurée pour garantir le succès de vos produits sur le marché canadien.
          </p>

          <div className="relative">
            {/* Connection Line */}
            <div className="absolute left-1/2 top-0 bottom-0 w-1 bg-primary/20 transform -translate-x-1/2 hidden md:block"></div>
            
            <div className="space-y-12">
              {workProcess.map((step, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  viewport={{ once: true }}
                  className={`flex flex-col ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'} items-center gap-8`}
                >
                  <div className="bg-white p-8 rounded-lg shadow-md md:w-1/2">
                    <h3 className="text-2xl font-bold mb-3">{step.title}</h3>
                    <p className="text-gray-600">{step.description}</p>
                  </div>
                  
                  <div className="flex items-center justify-center relative z-10 w-14 h-14 rounded-full bg-primary text-white font-bold text-xl">
                    {step.number}
                  </div>
                  
                  <div className="md:w-1/2"></div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Quality Standards */}
      <section className="section">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold mb-6">Nos Standards de Qualité</h2>
              <p className="text-gray-600 mb-6">
                Chez DZF Distribution, nous nous engageons à maintenir les plus hauts standards de qualité pour garantir la satisfaction de nos partenaires et clients finaux.
              </p>
              
              <div className="space-y-4">
                <div className="bg-white p-6 rounded-lg shadow-md">
                  <h4 className="font-bold text-xl mb-2">98% de conformité</h4>
                  <p>Nous maintenons un taux de conformité exceptionnellement élevé grâce à des procédures strictes et des contrôles rigoureux.</p>
                </div>
                
                <div className="bg-white p-6 rounded-lg shadow-md">
                  <h4 className="font-bold text-xl mb-2">Audits réguliers</h4>
                  <p>Nous effectuons des audits de qualité réguliers pour garantir le respect des normes tout au long de notre chaîne logistique.</p>
                </div>
                
                <div className="bg-white p-6 rounded-lg shadow-md">
                  <h4 className="font-bold text-xl mb-2">Traçabilité complète</h4>
                  <p>Tous nos produits sont entièrement traçables, de l'origine jusqu'au point de vente final.</p>
                </div>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <img 
                src="/images/quality-control.jpg" 
                alt="DZF Distribution Quality Control" 
                className="rounded-lg shadow-lg w-full h-auto"
              />
            </motion.div>
          </div>
        </div>
      </section>

      <CallToAction />
    </div>
  );
};

export default ServicesPage; 